import { VT_MONTHLY_CEILING_TTC } from "../../../Utils/VerifiedTrainerAmounts";
import type { MonthlyCeiling } from "./amounts";

/**
 * Clients « en relation Teach'r » : la mise en relation est faite par la
 * plateforme, qui fixe avec le client un tarif en deux parts dans la table
 * custom_fees : `pricePerHour` (ce que touche le prof) et `feesPerHour`
 * (les frais de la plateforme). Le client paie la somme des deux. Le prof ne
 * doit jamais voir la part de la plateforme : partout, on ne lui montre que
 * `pricePerHour` multiplié par le nombre d'heures.
 *
 * Côté API, la proposition de cours enregistre `teachrPrice = pricePerHour`
 * et `fees = feesPerHour`, sans passer par le taux `defaultFees` du prof.
 *
 * Deux formes de données portent cette information :
 * - la fiche d'un élève de la liste des clients validés : `isTeachrContact`,
 *   `pricePerHour`, `feesPerHour` à plat ;
 * - un cours du tableau de bord : objet `customFees` brut
 *   (`is_teachr_contact`, `price_per_hour`, `fees_per_hour`).
 */

const toNumber = (value: unknown): number => {
    const n = typeof value === "number" ? value : parseFloat(String(value ?? "").replace(",", "."));
    return Number.isFinite(n) ? n : 0;
};

const isFilled = (value: unknown): boolean => value !== null && value !== undefined && String(value) !== "";

/** Élève (liste des clients validés) dont le tarif est fixé par la plateforme. */
export const isRelationTeachrChild = (child: any): boolean =>
    !!child && !child.isTeachrContact && isFilled(child.pricePerHour) && isFilled(child.feesPerHour);

/** Cours du tableau de bord déclaré pour un tel client. */
export const isRelationTeachrCourse = (course: any): boolean => {
    const cf = course?.customFees;
    return !!cf && !cf.is_teachr_contact && isFilled(cf.price_per_hour) && isFilled(cf.fees_per_hour);
};

/** Tarif horaire du prof (`pricePerHour`), à partir d'une fiche élève ou d'un cours. */
export const relationHourlyRate = (source: any): number =>
    toNumber(source?.pricePerHour ?? source?.customFees?.price_per_hour);

/** Part horaire de la plateforme (`feesPerHour`). Jamais affichée : sert au plafond seulement. */
export const relationHourlyFees = (source: any): number =>
    toNumber(source?.feesPerHour ?? source?.customFees?.fees_per_hour);

export interface RelationRemuneration {
    hourly: number;
    total: number;
    hours: number;
}

/** Ce que le prof reçoit : son tarif horaire fois les heures, rien d'autre. */
export const relationRemuneration = (source: any, hours: number): RelationRemuneration => {
    const hourly = relationHourlyRate(source);
    return { hourly, hours, total: hourly * hours };
};

/** Phrase sous le tarif verrouillé, avec le nom de la marque. */
export const relationRateHint = (brandName: string, firstName: string): string =>
    `Tarif convenu avec ${brandName} pour ${firstName}, non modifiable ici.`;

/**
 * Plafond mensuel de l'avance immédiate pour un tel client : le montant qui
 * compte est ce que paie le client, tarif du prof plus part de la plateforme,
 * comparé aux 1 980 € du mois. Le résultat n'est jamais affiché en euros au
 * prof (voir `CeilingPanel` avec `hideAmounts`) : seule la barre et l'alerte
 * de dépassement lui sont montrées.
 *
 * `declared` reprend le montant déjà déclaré du mois tel que l'API le renvoie
 * (`amountForTheMonth`). Il ne compte aujourd'hui que la part des profs ; la
 * part de la plateforme des cours passés sera ajoutée côté API.
 */
export const relationCeiling = (source: any, hours: number, declaredInput: unknown, appliesToCurrentMonth: boolean): MonthlyCeiling => {
    const declared = toNumber(declaredInput);
    const thisDeclaration = (relationHourlyRate(source) + relationHourlyFees(source)) * hours;
    const ceiling = VT_MONTHLY_CEILING_TTC;
    const total = declared + thisDeclaration;
    const exceeds = appliesToCurrentMonth && thisDeclaration > 0 && Math.round(total * 100) / 100 > Math.round(ceiling * 100) / 100;
    return {
        declared,
        thisDeclaration,
        total,
        ceiling,
        exceeds,
        declaredRatio: ceiling > 0 ? Math.min(1, declared / ceiling) : 0,
        thisRatio: ceiling > 0 ? Math.min(1 - Math.min(1, declared / ceiling), thisDeclaration / ceiling) : 0,
    };
};
